import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import toast, { Toaster } from 'react-hot-toast';
import { ArrowLeftIcon, PlayIcon, StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { StarIcon } from '@heroicons/react/24/outline';

type Step = 'auth' | 'compose' | 'sent' | 'registered';

const MessagePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Registration context (fan just followed the creator from bio page)
  const isRegistered = searchParams.get('registered') === '1';
  const registrationCoupon = searchParams.get('coupon') || '';

  const [talent, setTalent] = useState<any>(null);
  const [bioSettings, setBioSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<Step>(isRegistered ? 'registered' : 'auth');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [recentShoutouts, setRecentShoutouts] = useState<any[]>([]);
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);
  const [occasionIndex, setOccasionIndex] = useState(0);
  const [randomReview, setRandomReview] = useState<any>(null);

  // 24-hour countdown for the registration coupon
  const couponExpiresAt = useRef<Date>(new Date(Date.now() + 24 * 60 * 60 * 1000));
  const [countdown, setCountdown] = useState('');
  useEffect(() => {
    if (!isRegistered) return;
    const tick = () => {
      const diff = couponExpiresAt.current.getTime() - Date.now();
      if (diff <= 0) { setCountdown('expired'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(`${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [isRegistered]);

  // Auth
  const [email, setEmail] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [emailValid, setEmailValid] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isVerified, setIsVerified] = useState(false);

  // OTP
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpPhone, setOtpPhone] = useState('');
  const [otpPhoneHint, setOtpPhoneHint] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpNeedsPhone, setOtpNeedsPhone] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!username) return;
      let tp: any = null;
      const { data: byUsername } = await supabase
        .from('talent_profiles')
        .select('id, user_id, username, full_name, temp_full_name, temp_avatar_url, support_enabled, fan_shoutouts_enabled')
        .eq('username', username).single();
      if (byUsername) tp = byUsername;
      else if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(username)) {
        const { data: byId } = await supabase
          .from('talent_profiles')
          .select('id, user_id, username, full_name, temp_full_name, temp_avatar_url, support_enabled, fan_shoutouts_enabled')
          .eq('id', username).single();
        tp = byId;
      }
      if (!tp || (!tp.fan_shoutouts_enabled && !isRegistered)) { navigate(`/${username}`); return; }
      setTalent(tp);
      const { data: bs } = await supabase.from('bio_settings').select('*').eq('talent_id', tp.id).single();
      setBioSettings(bs);

      supabase
        .from('orders')
        .select('id, recipient_name, occasion, video_url, created_at')
        .eq('talent_id', tp.id)
        .eq('status', 'completed')
        .not('video_url', 'is', null)
        .order('completed_at', { ascending: false })
        .limit(6)
        .then(({ data }) => { if (data) setRecentShoutouts(data); });

      supabase
        .from('reviews')
        .select('id, rating, comment, created_at, users!inner(full_name)')
        .eq('talent_id', tp.id)
        .gte('rating', 4)
        .not('comment', 'is', null)
        .limit(10)
        .then(({ data }) => {
          if (data && data.length > 0) {
            setRandomReview(data[Math.floor(Math.random() * data.length)]);
          }
        });

      const savedEmail = localStorage.getItem('bio_subscriber_email');
      const verified = localStorage.getItem('bio_account_verified') === 'true';
      if (savedEmail && verified) {
        setEmail(savedEmail);
        setEmailValid(true);
        setIsVerified(true);
        if (!isRegistered) setStep('compose');
      }
      setLoading(false);
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, navigate]);

  const displayName = bioSettings?.display_name || talent?.full_name || talent?.temp_full_name || 'Creator';
  const firstName = displayName.startsWith('The ') || displayName.startsWith('Chicks') ? displayName : displayName.split(' ')[0];
  const profileImage = bioSettings?.profile_image_url || talent?.temp_avatar_url;

  const shoutoutOccasions = [
    'the unforgettable birthday gift. 🎂',
    'the roast they\'ll never forget. 🔥',
    'a pep talk that actually hits. 💪',
    'the announcement they\'ll replay forever. 📣',
    'a thank you that means everything. 🙏',
    'the holiday surprise they didn\'t see coming. 🎁',
    'a graduation message worth framing. 🎓',
    'the ultimate fan moment. ⭐',
    'a get well message from the heart. ❤️',
    'wedding wishes they\'ll always remember. 💍',
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setOccasionIndex(prev => (prev + 1) % shoutoutOccasions.length);
    }, 3000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const gradStart = bioSettings?.gradient_start || '#0a0a0a';
  const gradEnd = bioSettings?.gradient_end || '#1a1a2e';

  const [emailLoading, setEmailLoading] = useState(false);

  const handleEmailSubmit = async () => {
    const v = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
    setEmailValid(v);
    if (!v) return;
    setEmailLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: email.toLowerCase().trim(), checkEmailOnly: true },
      });

      const resp = data || (error ? (() => { try { return JSON.parse((error as any)?.message || '{}'); } catch { return {}; } })() : {});

      if (resp.rateLimited) {
        toast.error('Please wait 60 seconds before requesting another code.');
        if (resp.phoneHint) {
          setOtpPhoneHint(resp.phoneHint);
          setOtpSent(true);
          setOtpNeedsPhone(false);
          setShowOtpModal(true);
        }
        return;
      }

      if (data?.sentToExistingPhone) {
        setOtpPhoneHint(data.phoneHint || '');
        setOtpPhone(data.phone || '');
        setOtpSent(true);
        setOtpNeedsPhone(false);
        setShowOtpModal(true);
      } else if (data?.needsPhone || (!data?.sentToExistingPhone && !error)) {
        setOtpNeedsPhone(true);
        setShowOtpModal(true);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleOtpSend = async () => {
    if (!otpPhone.trim()) { toast.error('Enter your phone number'); return; }
    setOtpLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: email.toLowerCase().trim(), phone: otpPhone },
      });
      if (error) throw error;
      setOtpPhoneHint(data?.phoneHint || otpPhone.slice(-4));
      setOtpSent(true);
      toast.success('Code sent!');
    } catch { toast.error('Failed to send code'); }
    finally { setOtpLoading(false); }
  };

  const handleOtpVerify = async () => {
    if (otpCode.length < 4) { toast.error('Enter the code'); return; }
    setOtpLoading(true);
    try {
      const { error } = await supabase.functions.invoke('verify-registration-otp', {
        body: { email: email.toLowerCase().trim(), phone: otpPhone, code: otpCode },
      });
      if (error) throw error;
      toast.success('Verified!');
      localStorage.setItem('bio_subscriber_email', email.toLowerCase().trim());
      localStorage.setItem('bio_account_verified', 'true');
      setIsVerified(true);
      setShowOtpModal(false);
      setStep('compose');
    } catch { toast.error('Invalid code'); }
    finally { setOtpLoading(false); }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) { toast.error('Please write a message'); return; }
    setSending(true);
    try {
      await supabase.functions.invoke('lunarpay-api', {
        body: {
          action: 'insert-donation-message',
          talentId: talent.id,
          donorEmail: email.toLowerCase(),
          donorName: email.split('@')[0],
          message: message.trim(),
          amount: 0,
          isMonthly: false,
          orderId: null,
        },
      });

      // Ensure user becomes a fan/follower
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .ilike('email', email.trim())
        .single();
      if (existingUser) {
        await supabase.from('talent_followers').upsert({
          user_id: existingUser.id,
          talent_id: talent.id,
        }, { onConflict: 'user_id,talent_id', ignoreDuplicates: true });

        supabase.functions.invoke('capture-lead', {
          body: { email: email.toLowerCase(), source: 'bio_page', talent_slug: talent.username || talent.id },
        }).catch(() => {});
      }

      toast.success('Message sent!');
      setStep('sent');
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(to bottom, ${gradStart}, ${gradEnd})` }}>
      <Toaster position="top-center" />
      <div className="max-w-lg mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate(`/${username}`)} className="flex items-center gap-2 text-gray-400 hover:text-white">
            <ArrowLeftIcon className="h-4 w-4" /><span className="text-sm">Back</span>
          </button>
          <img src="/shoutoutwhite.png" alt="ShoutOut" className="h-5 opacity-60" />
        </div>

        {/* Profile */}
        <div className="text-center mb-6">
          {profileImage && <img src={profileImage} alt="" className="w-20 h-20 rounded-full mx-auto mb-3 object-cover border-2 border-white/20" />}
          {step !== 'sent' && step !== 'registered' && (
            <>
              <h1 className="text-2xl font-bold text-white">ShoutOut {displayName}</h1>
              <p className="text-gray-400 text-sm mt-1">Send some encouragement, thanks, or feedback to {firstName}</p>
            </>
          )}
          {step === 'registered' && (
            <h1 className="text-2xl font-bold text-white">{displayName}</h1>
          )}
        </div>

        {/* Step: Auth */}
        {step === 'auth' && (
          <div className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleEmailSubmit(); }}
              placeholder="Enter your email to continue"
              className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-white/40"
            />
            <button
              onClick={handleEmailSubmit}
              disabled={emailLoading || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)}
              className="w-full py-3.5 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
            >
              {emailLoading ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : 'Continue'}
            </button>
            <p className="text-gray-500 text-xs text-center">We'll verify your identity with a quick code</p>
          </div>
        )}

        {/* Step: Compose */}
        {step === 'compose' && (
          <div className="space-y-4">
            <p className="text-gray-400 text-sm">Messaging as <span className="text-white font-medium">{email}</span></p>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={`Write a message to ${firstName}...`}
              maxLength={500}
              rows={5}
              className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-white/40 resize-none text-sm"
              autoFocus
            />
            <div className="flex items-center justify-between">
              <p className="text-gray-500 text-xs">These messages are public.</p>
              <p className="text-gray-500 text-xs">{message.length}/500</p>
            </div>
            <button
              onClick={handleSendMessage}
              disabled={sending || !message.trim()}
              className="w-full py-3.5 rounded-xl font-bold text-white bg-green-600 hover:bg-green-700 disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
            >
              {sending ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : 'Send Message'}
            </button>
          </div>
        )}

        {/* Step: Registered — post fan-follow CTAs */}
        {step === 'registered' && (() => {
          const shoutoutUrl = `/${talent?.username || username}/shoutout${registrationCoupon ? `?coupon=${encodeURIComponent(registrationCoupon)}` : ''}`;
          return (
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="text-4xl mb-2">💚</div>
                <h2 className="text-xl font-bold text-white">You're connected to {firstName}!</h2>
                <p className="text-gray-400 text-sm mt-1">{firstName} will see you in their fan list</p>
              </div>

              {/* ShoutOut CTA — first, with new title + 25% off countdown */}
              {talent?.fan_shoutouts_enabled && (
                <a href={shoutoutUrl} className="block">
                  <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl overflow-hidden border border-blue-500/30 hover:border-blue-500/50 transition-all duration-300 hover:scale-[1.02]">
                    <div className="flex items-stretch">
                      <div className="w-20 flex-shrink-0">
                        {profileImage ? (
                          <img src={profileImage} alt={displayName} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                            {displayName[0]}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 p-4 flex flex-col justify-center">
                        <p className="text-white text-sm font-bold leading-snug mb-1.5">
                          Get a Personalized Video From {firstName}.
                        </p>
                        {countdown && countdown !== 'expired' && (
                          <p className="text-amber-300 text-sm font-bold mb-1">
                            <span className="text-white/70 font-normal">New Fan Discount: </span>25% off for{' '}
                            <span className="text-amber-300 tabular-nums">{countdown}</span>
                          </p>
                        )}
                        {randomReview ? (
                          <div className="mt-1 pt-1.5 border-t border-white/10">
                            <div className="flex items-center gap-1 mb-0.5">
                              {[...Array(5)].map((_, i) => (
                                i < randomReview.rating ? (
                                  <StarSolidIcon key={i} className="h-3 w-3 text-yellow-400" />
                                ) : (
                                  <StarIcon key={i} className="h-3 w-3 text-gray-600" />
                                )
                              ))}
                            </div>
                            {randomReview.comment && (
                              <p className="text-gray-300 text-xs line-clamp-1">
                                "{randomReview.comment.split('\n')[0]}"
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-gray-400 text-[10px]">From {displayName}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </a>
              )}

              {/* Support CTA — second */}
              <a href={`/${talent?.username || username}/support?skipMessage=true`} className="block">
                <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-2xl overflow-hidden border border-green-500/30 hover:border-green-500/50 transition-all duration-300 hover:scale-[1.02] p-5">
                  <h3 className="text-white font-semibold text-base mb-2">Support {firstName} With a Donation</h3>
                  <ul className="space-y-1.5">
                    <li className="flex items-center gap-2 text-gray-300 text-sm">
                      <span className="text-green-400">✓</span> Get a guaranteed response from {firstName}
                    </li>
                    <li className="flex items-center gap-2 text-gray-300 text-sm">
                      <span className="text-green-400">✓</span> Your message shown on {firstName}'s page
                    </li>
                    <li className="flex items-center gap-2 text-gray-300 text-sm">
                      <span className="text-green-400">✓</span> Rank higher in {firstName}'s top fans list by supporting!
                    </li>
                    <li className="flex items-center gap-2 text-gray-300 text-sm">
                      <span className="text-green-400">✓</span> Earn 1 point per $1 donated and show up higher on {firstName}'s Top Fans
                    </li>
                  </ul>
                </div>
              </a>

              {/* Recent ShoutOuts Video Feed */}
              {recentShoutouts.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-white font-semibold text-sm mb-3">ShoutOuts {firstName} has sent other fans.</h3>
                  <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory" style={{ scrollbarWidth: 'none' }}>
                    {recentShoutouts.map(shoutout => (
                      <div key={shoutout.id} className="flex-shrink-0 w-40 snap-start">
                        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                          <div className="aspect-[9/16] relative bg-black">
                            <video
                              src={`${shoutout.video_url}#t=0.5`}
                              className="w-full h-full object-cover"
                              playsInline
                              preload="auto"
                              onClick={(e) => {
                                const vid = e.currentTarget;
                                if (vid.paused) { vid.muted = false; vid.play(); setPlayingVideoId(shoutout.id); }
                                else { vid.pause(); setPlayingVideoId(null); }
                              }}
                              onEnded={() => setPlayingVideoId(null)}
                            />
                            {playingVideoId !== shoutout.id && (
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-10 h-10 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                                  <PlayIcon className="h-5 w-5 text-white ml-0.5" />
                                </div>
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 p-2 pointer-events-none">
                              <p className="text-white text-xs font-medium truncate">For {shoutout.recipient_name || 'a fan'}</p>
                              <p className="text-gray-400 text-[10px]">{shoutout.occasion || 'ShoutOut'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Step: Sent — CTAs */}
        {step === 'sent' && (
          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="text-4xl mb-2">💚</div>
              <h2 className="text-xl font-bold text-white">Message Sent!</h2>
              <p className="text-gray-400 text-sm mt-1">{firstName} will see your message</p>
            </div>

            {/* ShoutOut CTA — first */}
            <a
              href={`/${talent?.username || username}/shoutout`}
              className="block"
            >
              <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl overflow-hidden border border-blue-500/30 hover:border-blue-500/50 transition-all duration-300 hover:scale-[1.02]">
                <div className="flex items-stretch">
                  <div className="w-20 flex-shrink-0">
                    {profileImage ? (
                      <img src={profileImage} alt={displayName} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                        {displayName[0]}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 p-4 flex flex-col justify-center">
                    <p className="text-blue-400 text-sm font-semibold mb-1">
                      Get a personalized video ShoutOut for...
                    </p>
                    <div className="h-5 overflow-hidden relative">
                      {shoutoutOccasions.map((phrase, i) => (
                        <div
                          key={i}
                          className="absolute inset-x-0 transition-all duration-500 ease-in-out"
                          style={{
                            transform: i === occasionIndex ? 'translateY(0)' : i === (occasionIndex - 1 + shoutoutOccasions.length) % shoutoutOccasions.length ? 'translateY(-100%)' : 'translateY(100%)',
                            opacity: i === occasionIndex ? 1 : 0,
                          }}
                        >
                          <p className="text-white text-sm font-semibold">{phrase}</p>
                        </div>
                      ))}
                    </div>
                    {randomReview ? (
                      <div className="mt-2 pt-2 border-t border-white/10">
                        <div className="flex items-center gap-1 mb-0.5">
                          {[...Array(5)].map((_, i) => (
                            i < randomReview.rating ? (
                              <StarSolidIcon key={i} className="h-3 w-3 text-yellow-400" />
                            ) : (
                              <StarIcon key={i} className="h-3 w-3 text-gray-600" />
                            )
                          ))}
                        </div>
                        {randomReview.comment && (
                          <p className="text-gray-300 text-xs line-clamp-1">
                            "{randomReview.comment.split('\n')[0]}"
                          </p>
                        )}
                        {randomReview.users?.full_name && (
                          <p className="text-gray-500 text-[10px] mt-0.5">
                            — {randomReview.users.full_name}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-[10px] mt-1">
                        From {displayName}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </a>

            {/* Support CTA — second */}
            <a
              href={`/${talent?.username || username}/support?skipMessage=true`}
              className="block"
            >
              <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-2xl overflow-hidden border border-green-500/30 hover:border-green-500/50 transition-all duration-300 hover:scale-[1.02] p-5">
                <h3 className="text-white font-semibold text-base mb-2">Support {firstName} With a Donation</h3>
                <ul className="space-y-1.5">
                  <li className="flex items-center gap-2 text-gray-300 text-sm">
                    <span className="text-green-400">✓</span> Get a guaranteed response from {firstName}
                  </li>
                  <li className="flex items-center gap-2 text-gray-300 text-sm">
                    <span className="text-green-400">✓</span> Your message shown on {firstName}'s page
                  </li>
                </ul>
              </div>
            </a>

            {/* Recent ShoutOuts Video Feed */}
            {recentShoutouts.length > 0 && (
              <div className="mt-6">
                <h3 className="text-white font-semibold text-sm mb-3">ShoutOuts {firstName} has sent other fans.</h3>
                <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory" style={{ scrollbarWidth: 'none' }}>
                  {recentShoutouts.map(shoutout => (
                    <div key={shoutout.id} className="flex-shrink-0 w-40 snap-start">
                      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                        <div className="aspect-[9/16] relative bg-black">
                          <video
                            src={`${shoutout.video_url}#t=0.5`}
                            className="w-full h-full object-cover"
                            playsInline
                            preload="auto"
                            onClick={(e) => {
                              const vid = e.currentTarget;
                              if (vid.paused) {
                                vid.muted = false;
                                vid.play();
                                setPlayingVideoId(shoutout.id);
                              } else {
                                vid.pause();
                                setPlayingVideoId(null);
                              }
                            }}
                            onEnded={() => setPlayingVideoId(null)}
                          />
                          {playingVideoId !== shoutout.id && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="w-10 h-10 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                                <PlayIcon className="h-5 w-5 text-white ml-0.5" />
                              </div>
                            </div>
                          )}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 p-2 pointer-events-none">
                            <p className="text-white text-xs font-medium truncate">For {shoutout.recipient_name || 'a fan'}</p>
                            <p className="text-gray-400 text-[10px]">{shoutout.occasion || 'ShoutOut'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="pt-6 flex items-center justify-center gap-3">
          <p className="text-gray-600 text-xs">Powered by ShoutOut</p>
          {email && (
            <button onClick={() => { localStorage.removeItem('bio_subscriber_email'); localStorage.removeItem('bio_account_verified'); window.location.reload(); }} className="text-gray-600 text-xs hover:text-gray-400 transition-colors">Logout</button>
          )}
        </div>
      </div>

      {/* OTP Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-white text-center mb-1">Verify Your Identity</h3>
            <p className="text-gray-400 text-sm text-center mb-5">Quick verification to send your message</p>
            {!otpSent && otpNeedsPhone ? (
              <div className="space-y-3">
                <input type="tel" value={otpPhone} onChange={e => setOtpPhone(e.target.value)} placeholder="Phone number"
                  className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none text-center text-lg" />
                <button onClick={handleOtpSend} disabled={otpLoading}
                  className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {otpLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : 'Send Code'}
                </button>
              </div>
            ) : otpSent ? (
              <div className="space-y-3">
                <p className="text-gray-400 text-sm text-center">Code sent to {otpPhoneHint || '***'}</p>
                <input type="text" value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" maxLength={6} autoFocus
                  className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white text-center text-2xl tracking-[0.5em] placeholder-gray-500 focus:outline-none font-mono"
                  onKeyDown={e => { if (e.key === 'Enter') handleOtpVerify(); }} />
                <button onClick={handleOtpVerify} disabled={otpLoading}
                  className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {otpLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : 'Verify & Continue'}
                </button>
                <button onClick={() => { setOtpSent(false); setOtpCode(''); setOtpNeedsPhone(true); }}
                  className="w-full text-center text-sm text-gray-400 hover:text-white">Change number</button>
              </div>
            ) : (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
              </div>
            )}
            <button onClick={() => setShowOtpModal(false)}
              className="w-full mt-4 text-center text-sm text-gray-500 hover:text-white">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagePage;
